/**
 * Le panneau de période — les deux bornes du planning général et des tableaux de bord.
 *
 * *Demande du métier, 16/08/2026 : « par défaut se mettre sur période J-7 à J+80 à
 * partir de la date du jour. Mettre un panneau pour pouvoir borner les dates
 * manuellement en cliquant sur les dates dans un calendrier. »*
 *
 * ## Deux dates, pas un nombre de semaines
 *
 * Les deux écrans offraient « 3 semaines / 4 semaines / 13 semaines » à partir
 * d'aujourd'hui. Ça répond à « que se passe-t-il bientôt » et à rien d'autre : impossible
 * de regarder le mois de novembre, ni de revenir sur la semaine dernière. Deux dates
 * répondent aux trois questions, et se lisent sans conversion mentale.
 *
 * `<input type="date">` et non un calendrier dessiné à la main : c'est le calendrier du
 * système, celui que l'utilisateur connaît déjà, et il s'ouvre au clic sur le champ. En
 * écrire un serait plus joli et moins bon — pas de saisie au clavier, pas de raccourcis,
 * et un comportement à maintenir sur trois navigateurs.
 *
 * ## Le défaut vient du SERVEUR
 *
 * L'écran n'invente jamais les dates par défaut : il appelle sans bornes, et remplit ses
 * deux champs avec ce que la réponse annonce. Calculer « aujourd'hui − 7 » ici le
 * calculerait dans le fuseau du poste, quand tout le reste du produit lit le jour au
 * même endroit — et un poste réglé sur la veille afficherait un horizon décalé sans que
 * rien ne le dise.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterDate } from "@planif/moteur";

export type Bornes = { readonly debut: string; readonly fin: string };

/**
 * `14:32:07` — l'heure d'une lecture, dans le fuseau du poste.
 *
 * **Les SECONDES en font partie, et ce n'est pas du zèle.** Vu à l'écran le 21/09/2026 : à la
 * minute près, actualiser deux fois de suite laisse l'heure identique, et le bouton se lit
 * comme un bouton en panne — exactement ce que l'horodatage devait empêcher. La lecture dure
 * moins d'une seconde, donc l'état « Actualisation… » ne se voit pas davantage : les secondes
 * sont la seule preuve qui reste que le geste a servi.
 *
 * Écrite à la main plutôt que par `Intl` : la même raison qu'ailleurs dans ce produit, un
 * format qui ne dépend pas de la langue du système. Deux chiffres partout, sans quoi « 9:5:7 »
 * se lirait de travers.
 */
export function heureCourte(instant: Date): string {
  const deux = (valeur: number): string => String(valeur).padStart(2, "0");
  return `${deux(instant.getHours())}:${deux(instant.getMinutes())}:${deux(instant.getSeconds())}`;
}

export function PanneauPeriode({
  affichee,
  chargement,
  onBorner,
  onDefaut,
  onActualiser,
  actualiseA,
}: {
  /** La période réellement affichée, telle que le serveur l'a rendue. */
  affichee: Bornes | null;
  chargement: boolean;
  onBorner: (bornes: Bornes) => void;
  /** Revenir à l'horizon par défaut — c'est le serveur qui le décide, pas cet écran. */
  onDefaut: () => void;
  /**
   * Relire les données sans changer la période *(Chris, 21/09/2026)*.
   *
   * **Facultatif, et absent partout ailleurs.** La demande porte sur le planning des
   * ressources, qui est une fenêtre qu'on garde ouverte pendant qu'on affecte dans l'autre :
   * elle vieillit sous les yeux, et la seule façon de la remettre à jour était de la fermer et
   * de la rouvrir. Les tableaux de bord, eux, se rechargent au moindre geste — un bouton de
   * plus y serait un bouton dont personne ne saurait quand il sert.
   */
  onActualiser?: () => void;
  /**
   * L'heure de la dernière lecture, pour que le bouton dise qu'il a fait quelque chose.
   *
   * Sans elle, actualiser un planning qui n'a pas changé ne produit rien à l'écran, et le
   * bouton se lit comme un bouton en panne — c'est le geste dont on a le plus besoin qu'il
   * rende des comptes, puisqu'on le presse justement quand on doute de ce qu'on lit.
   *
   * C'est l'horloge du POSTE, et c'est le seul endroit du produit où elle est légitime : elle
   * ne date aucune donnée métier, elle date une action de celui qui regarde.
   */
  actualiseA?: Date | null;
}): JSX.Element {
  /**
   * Les deux champs sont un état LOCAL, resynchronisé sur la réponse.
   *
   * Sans état local, taper une date la renverrait au serveur à chaque frappe — et une
   * année saisie chiffre par chiffre passerait par « 0002 », donc par une période de
   * deux mille ans que le serveur refuserait. On applique au changement terminé.
   */
  const [debut, setDebut] = useState("");
  const [fin, setFin] = useState("");

  useEffect(() => {
    if (affichee === null) return;
    setDebut(affichee.debut);
    setFin(affichee.fin);
  }, [affichee]);

  const modifiee =
    affichee !== null && (debut !== affichee.debut || fin !== affichee.fin);
  // Une borne vide n'est pas une erreur de saisie, c'est une saisie en cours : le
  // bouton attend, il ne proteste pas.
  const applicable = debut !== "" && fin !== "" && debut <= fin && modifiee;

  return (
    <div className="periode">
      {/*
        **Actualiser, à gauche du bandeau** — *demande de Chris, 21/09/2026 : « quand nous sommes
        dans le planning des ressources il faudrait pouvoir actualiser les informations sans
        avoir à sortir puis rouvrir ; peux-tu créer un bouton actualiser à gauche dans le bandeau
        où on peut paramétrer les périodes ».*

        Avant les deux dates, et pas après : c'est le geste le plus fréquent des trois, et le
        seul qui ne demande rien à saisir.
      */}
      {onActualiser !== undefined && (
        <button
          type="button"
          className="periode__actualiser"
          disabled={chargement}
          title="Relire le plan, les affectations, les congés et les immobilisations — sans changer la période"
          onClick={onActualiser}
        >
          {chargement ? "Actualisation…" : "↻ Actualiser"}
        </button>
      )}

      <span className="periode__titre">Période</span>
      <label className="periode__champ">
        du
        <input
          type="date"
          value={debut}
          disabled={chargement}
          onChange={(e) => {
            setDebut(e.target.value);
          }}
        />
      </label>
      <label className="periode__champ">
        au
        <input
          type="date"
          value={fin}
          disabled={chargement}
          onChange={(e) => {
            setFin(e.target.value);
          }}
        />
      </label>

      <button
        type="button"
        className="principal"
        disabled={!applicable || chargement}
        onClick={() => {
          onBorner({ debut, fin });
        }}
      >
        Afficher
      </button>

      <button
        type="button"
        className="lien"
        disabled={chargement}
        title="Sept jours en arrière, quatre-vingts en avant, à partir d'aujourd'hui"
        onClick={onDefaut}
      >
        Par défaut (J-7 → J+80)
      </button>

      {/*
        Ce qui est AFFICHÉ, écrit à côté de ce qui est saisi. Les deux diffèrent dès
        qu'on touche un champ sans valider, et sans ce rappel on lirait la frise en
        croyant qu'elle porte déjà les dates qu'on vient de taper.
      */}
      {affichee !== null && (
        <span className="periode__affichee">
          affiché : {formaterDate(affichee.debut)} → {formaterDate(affichee.fin)}
          {modifiee && <strong> · saisie non appliquée</strong>}
          {actualiseA != null && (
            <span
              className="note"
              title="Heure de la dernière lecture. Ce qui a été affecté depuis n'est pas encore là."
            >
              {" "}
              · lu à {heureCourte(actualiseA)}
            </span>
          )}
        </span>
      )}

      {debut !== "" && fin !== "" && debut > fin && (
        <span className="periode__refus" role="alert">
          La fin précède le début.
        </span>
      )}
    </div>
  );
}
